import { useEffect } from 'react';
import { useMutation } from '@apollo/client';

import { PageTypes } from 'utils/pageTypes';
import { CASE_ACTIONS, AppRoutePath } from 'config/constants';
import { useCaseSync } from 'hooks/useCaseSync';
import { openPopup } from 'utils/openPopup';
import { GET_ME, UPDATE_LAST_VIEWED_WORKLIST_ITEM_SMIDS } from 'modules/Apollo/queries';
import type {
  GetMeQuery,
  GetMeQueryVariables,
  UpdateLastViewedWorklistItemSmidsMutation,
  UpdateLastViewedWorklistItemSmidsMutationVariables,
} from 'generated/graphql';
import { usePopupOptionsGetter } from 'hooks/useWindowsRects';
import { useSendFocusEvent } from '../modules/focusManager';
import { logEvents } from '../common/Navbar/MenuItem';
import { atom, useRecoilCallback, useSetRecoilState } from 'recoil';
import type { RecoilState, Snapshot } from 'recoil';
import { useWorkspacePresets } from 'hooks/usePreferences';
import {
  applyWorkspacePresetEvent,
  openCaseEvent,
  dispatchExtensionEvent,
} from 'domains/extension/extensionEventCreators';
import { computeOpenTabs, useOpenTabs } from './useOpenTabs';
import { useToasterDispatch } from '../common/ui/Toaster/Toaster';
import { ScreenName } from '../generated/graphql';
import { useUrlBuilder } from './useUrlBuilder';
import { useStudies } from 'domains/viewer/Viewer/StudyLoader/useStudies';
import { useCurrentCaseId } from './useCurrentCase';
import { extensionAppState } from './useExtensionState';
import { systemCheckPreferenceState } from 'common/SystemCheck/useSystemCheck';
import { logger } from 'modules/logger';
import { WithApolloContext } from 'modules/Apollo/types';
import { getViewerWindowIdFromUrl } from 'utils/viewerUrlUtils';

export const popupLockAtom: RecoilState<boolean> = atom({
  key: 'popupLockAtom',
  default: false,
});

export type OpenCaseOpts = {
  smid: string;
  action: (typeof CASE_ACTIONS)[keyof typeof CASE_ACTIONS];
  studyIds?: ReadonlyArray<string>;
};

export type OpenCase = {
  openCase: (opts: OpenCaseOpts) => Promise<void>;
};

type UseOpenCaseOpts = WithApolloContext & {
  reporterOnly?: boolean;
  patientJacketOnRead?: boolean;
  patientJacketDocked?: boolean;
};

const toastKey = 'useOpenCase';

export const useOpenCase = ({
  reporterOnly = false,
  patientJacketOnRead = false,
  patientJacketDocked = false,
  context,
}: UseOpenCaseOpts): OpenCase => {
  const syncCase = useCaseSync({ context });
  const sendEvent = useSendFocusEvent();
  const [updateLastViewedWorklistItemSmids] = useMutation<
    UpdateLastViewedWorklistItemSmidsMutation,
    UpdateLastViewedWorklistItemSmidsMutationVariables
  >(UPDATE_LAST_VIEWED_WORKLIST_ITEM_SMIDS, {
    context,
  });

  const { enqueueOrUpdateToast } = useToasterDispatch();

  const getPopupOptions = usePopupOptionsGetter();
  const setPopupLock = useSetRecoilState(popupLockAtom);
  const { autoApply, mostRecentPresetWithInstanceId } = useWorkspacePresets({ context });
  const { isViewer0Open, isViewer1Open, isReporterOpen } = useOpenTabs();
  const buildViewerUrl = useUrlBuilder({ isViewer: true });
  const buildReporterUrl = useUrlBuilder({ isReporter: true });
  const currentCaseId = useCurrentCaseId();
  const { autoloadedStudiesIds } = useStudies({ context });

  const openCase = useRecoilCallback(
    ({ snapshot, set }) =>
      async ({ smid, action, studyIds }: OpenCaseOpts) => {
        const extensionState = await snapshot.getPromise(extensionAppState);
        const systemCheckPreference = await snapshot.getPromise(systemCheckPreferenceState);

        const { isViewer0Open, isViewer1Open, isReporterOpen } = computeOpenTabs(
          extensionState,
          systemCheckPreference
        );

        syncCase(smid, studyIds);
        logger.info(`[useOpenCase] openCase ${smid}`);
        dispatchExtensionEvent(openCaseEvent({ smid }));

        // Remember to call this to update the user profile
        updateLastViewedWorklistItemSmids({
          variables: { smids: [smid] },
          update: (proxy) => {
            const data = proxy.readQuery<GetMeQuery, GetMeQueryVariables>({ query: GET_ME });

            if (data != null) {
              proxy.writeQuery({
                query: GET_ME,
                data: {
                  me: {
                    ...data?.me,
                    __typename: 'User',
                    lastViewedWorklistItemSmids: [smid],
                  },
                },
              });
              return;
            }
          },
        });

        const toApply = mostRecentPresetWithInstanceId;

        const shouldOpenViewer1 =
          toApply?.windows.some((wind: any) => wind.screen === ScreenName.Viewer1) && autoApply;

        const autoloadedStudySmids = smid === currentCaseId ? autoloadedStudiesIds : [];

        const viewerUrl0 = buildViewerUrl({
          caseSmid: smid,
          viewerId: '0',
          studySmids: studyIds,
          autoloadedStudySmids,
        });
        const viewerUrl1 = buildViewerUrl({
          caseSmid: smid,
          viewerId: '1',
          studySmids: studyIds,
          autoloadedStudySmids,
        });
        const reporterUrl = buildReporterUrl({
          caseSmid: smid,
          studySmids: studyIds,
          autoloadedStudySmids,
        });

        // this operation uses a cross window recoil atom to lock the open popup operation
        // this prevents the user from opening multiple popups at the same time
        const openPopupWithLock = async (snapshot: Snapshot, url: string) => {
          const popupLock = await snapshot.getPromise(popupLockAtom);

          if (popupLock) return true;

          const viewerWindowId = getViewerWindowIdFromUrl(url);

          const openingOpenReporter = isReporterOpen && url.includes('reporter');
          const openingOpenViewer0 = isViewer0Open && viewerWindowId === '0';
          const openingOpenViewer1 = isViewer1Open && viewerWindowId === '1';

          if (openingOpenReporter || openingOpenViewer0 || openingOpenViewer1) return true;

          set(popupLockAtom, true);
          return await openPopup(url, getPopupOptions(url));
        };

        const loadCaseError = (window: string) => {
          const message = `A popup blocker prevented the application from opening the ${window}, try disabling the popup blocker and try again.`;

          enqueueOrUpdateToast(message, toastKey, {
            severity: 'error',
          });
        };

        if (reporterOnly) {
          const loadCaseSuccess = () => {
            const message = isReporterOpen
              ? `Your exam has been loaded on the existing Reporter`
              : `Your exam has been loaded on a new Reporter`;

            enqueueOrUpdateToast(message, toastKey, {
              severity: 'success',
            });
          };

          if ([CASE_ACTIONS.VIEW, CASE_ACTIONS.READ].includes(action)) {
            if (!isReporterOpen && reporterUrl != null) {
              const success = await openPopupWithLock(snapshot, reporterUrl);
              if (success) {
                loadCaseSuccess();
                if (patientJacketOnRead && !patientJacketDocked) {
                  const jacketUrl = AppRoutePath.PATIENT_JACKET_UNDOCKED.replace(':smid', smid);
                  await openPopupWithLock(snapshot, jacketUrl);
                }
              } else {
                loadCaseError('Reporter');
              }
            }
          }

          return;
        }

        const openPromises: Promise<['Viewer' | 'Reporter', boolean]>[] = [];
        if (action === CASE_ACTIONS.VIEW || action === CASE_ACTIONS.READ) {
          if (!isViewer0Open && viewerUrl0 != null) {
            const promise = openPopupWithLock(snapshot, viewerUrl0).then(
              (success): ['Viewer' | 'Reporter', boolean] => ['Viewer', success]
            );
            openPromises.push(promise);
            logEvents({ pageType: PageTypes.VIEWER, sendEvent, viewerIndex: '0' });
          }
          if (!isViewer1Open && shouldOpenViewer1 && viewerUrl1 != null) {
            const promise = openPopupWithLock(snapshot, viewerUrl1).then(
              (success): ['Viewer' | 'Reporter', boolean] => ['Viewer', success]
            );
            openPromises.push(promise);
            logEvents({ pageType: PageTypes.VIEWER, sendEvent, viewerIndex: '0' });
          }
        }
        if (action === CASE_ACTIONS.READ) {
          if (!isReporterOpen && reporterUrl != null) {
            const promise = openPopupWithLock(snapshot, reporterUrl).then(
              (success): ['Viewer' | 'Reporter', boolean] => ['Reporter', success]
            );
            openPromises.push(promise);
          }
        }

        const results = await Promise.all(openPromises);

        const maybeFailedResult = results.find(([_type, success]) => !success);
        if (maybeFailedResult != null) {
          if (
            results.some(([type]) => type === 'Viewer') &&
            results.some(([type]) => type === 'Reporter')
          ) {
            loadCaseError('Viewer and Reporter');
          } else {
            loadCaseError(maybeFailedResult[0]);
          }

          return;
        }

        if (results.every(([_type, success]) => success)) {
          const message = generateToastMessage(
            action,
            isReporterOpen,
            isViewer0Open,
            isViewer1Open,
            shouldOpenViewer1
          );

          enqueueOrUpdateToast(message, toastKey, {
            severity: 'success',
          });

          if (patientJacketOnRead && !patientJacketDocked) {
            const jacketUrl = AppRoutePath.PATIENT_JACKET_UNDOCKED.replace(':smid', smid);
            await openPopupWithLock(snapshot, jacketUrl);
          }
        }

        if (toApply != null && autoApply) {
          // apply workspace preset
          logger.info(`[useOpenCase] auto apply workspace preset ${toApply.name} to case ${smid}`);
          dispatchExtensionEvent(applyWorkspacePresetEvent({ preset: toApply, caseId: smid }));
        }
      },

    [
      syncCase,
      updateLastViewedWorklistItemSmids,
      mostRecentPresetWithInstanceId,
      autoApply,
      currentCaseId,
      autoloadedStudiesIds,
      buildViewerUrl,
      buildReporterUrl,
      reporterOnly,
      getPopupOptions,
      enqueueOrUpdateToast,
      patientJacketOnRead,
      patientJacketDocked,
      sendEvent,
    ]
  );

  useEffect(() => {
    if (isViewer0Open || isViewer1Open || isReporterOpen) {
      setPopupLock(false);
    }
  }, [isViewer0Open, isViewer1Open, isReporterOpen, setPopupLock, reporterOnly]);

  return {
    openCase,
  };
};

function generateToastMessage(
  action: 'READ' | 'VIEW',
  isReporterOpen: boolean,
  isViewer0Open: boolean,
  isViewer1Open: boolean,
  shouldOpenViewer1: boolean
): string {
  if (action === CASE_ACTIONS.VIEW) {
    if (isViewer0Open && isReporterOpen) {
      // only Viewers should open, but the Reporter might already be open
      return `Your exam has been loaded on the existing Viewer${isViewer1Open ? 's' : ''} and Reporter`;
    } else if (isViewer0Open) {
      // load Viewer(s)
      return `Your exam has been loaded on the existing Viewer${isViewer1Open ? 's' : ''}`;
    } else if (isReporterOpen) {
      // we have a Reporter for some reason! Load the Viewers
      return `Your exam has been loaded on the existing Reporter and in ${
        shouldOpenViewer1 ? 'new Viewers' : 'a new Viewer'
      }`;
    }
    // open new Viewer(s)
    return `Your exam has been loaded on ${shouldOpenViewer1 ? 'new Viewers' : 'a new Viewer'}`;
  } else {
    if (isViewer0Open && isReporterOpen) {
      // most/everything is already open
      return `Your exam has been loaded on the existing Viewer${isViewer1Open ? 's' : ''} and Reporter`;
    } else if (isViewer0Open) {
      // need to open Reporter
      return `Your exam has been loaded on the existing Viewer${
        isViewer1Open ? 's' : ''
      } and in a new Reporter`;
    } else if (isReporterOpen) {
      // need to open Viewer(s)
      return `Your exam has been loaded on the existing Reporter and in ${
        shouldOpenViewer1 ? 'new Viewers' : 'a new Viewer'
      }`;
    }
    // open everything
    return `Your exam has been loaded on ${
      shouldOpenViewer1 ? 'new Viewers and a new' : 'a new Viewer and'
    } Reporter`;
  }
}
